import { useState } from "react"
import { Card, Container, Row } from "react-bootstrap"
import { ChartRange } from "@components/ChartRangeSelector"
import { ImpressionsByNetwork } from "./charts/ImpressionsByNetwork"
import { ImpressionsByNetworkBar } from "./charts/ImpressionsByNetworkBar"
import ConversionsHud from "./charts/ConversionsHud"
import { EngagementsByNetworkBar } from "./charts/EngagementsByNetworkBar"
import { EngagementsByDay } from "./charts/EngagementsByDay"
import { ConversionsByJourneyStep } from "./charts/ConversionsByJourneyStep"
import ConversionsByDayByStep from "./charts/ConversionsByDayByStep"
import CostByNetworkTable from "./charts/CostByNetworkTable"
import CostTrend from "./charts/CostTrend"
import ReachByNetwork from "./charts/ReachByNetwork"

/*
    Conversions tab (MPN-1530 §3).

    Grouped into the bands the supplied mockup uses -- the outcome, then what it cost,
    then exposure, then reach. The ticket text does not mention the dividers, but the
    mockup has them and the summary asks for "clearer data groupings"; without them this
    tab is eight unrelated cards in a column and the reader has to know which numbers
    belong together.

    Conversions By Day is now stacked by journey step (§3.2) and replaces the plain
    total-per-day chart, which the Performance Snapshot's sparkline already carries.
*/

function Divider({ title, note }: { title: string; note: string }) {
  return (
    <div className="mon-divider">
      <span className="mon-divider__title">{title}</span>
      <em className="mon-divider__note">{note}</em>
    </div>
  )
}

const Conversions = ({ data }: { data: any }) => {
  const [networkRange] = useState<ChartRange>(ChartRange.All)

  return (
    <Container className="d-flex flex-column gap-3 mb-5">
      <Row>
        <div className="d-flex gap-2 ps-0 flex-wrap">
          <ConversionsHud data={data} />
        </div>
      </Row>

      <Divider title="Conversions" note="the outcome that matters most" />

      <Row className="flex-grow-1">
        <Card className="custom-card border border-2 mb-0">
          <Card.Header>
            <Card.Title>Conversions By Day</Card.Title>
          </Card.Header>
          <Card.Body>
            <ConversionsByDayByStep data={data} />
          </Card.Body>
        </Card>
      </Row>

      <Row className="flex-grow-1">
        <Card className="custom-card border border-2 mb-0">
          <Card.Header>
            <Card.Title>Conversions By Journey Step</Card.Title>
          </Card.Header>
          <Card.Body>
            <ConversionsByJourneyStep data={data} />
          </Card.Body>
        </Card>
      </Row>

      <Divider title="Cost efficiency" note="what it cost to get there" />

      <Row className="flex-grow-1">
        <Card className="custom-card overflow-hidden mb-0 border border-2">
          <Card.Header>
            <Card.Title>CPC &amp; CPM By Network</Card.Title>
          </Card.Header>
          <Card.Body>
            <CostByNetworkTable data={data} />
          </Card.Body>
        </Card>
      </Row>

      <Row className="flex-grow-1">
        <Card className="custom-card overflow-hidden mb-0 border border-2">
          <Card.Header>
            <Card.Title>CPC &amp; CPM Trend</Card.Title>
          </Card.Header>
          <Card.Body>
            <CostTrend data={data} />
          </Card.Body>
        </Card>
      </Row>

      <Divider title="Impressions &amp; engagement" note="exposure metrics" />

      <Row className="flex-grow-1">
        <Card className="custom-card overflow-hidden mb-0 border border-2">
          <Card.Header className="justify-content-between">
            <Card.Title>Impressions By Network</Card.Title>
          </Card.Header>
          <Card.Body>
            <ImpressionsByNetworkBar series={data["impressions-by-network"]} />
          </Card.Body>
        </Card>
      </Row>

      <Row className="flex-grow-1">
        <Card className="custom-card overflow-hidden mb-0 border border-2">
          <div className="card-header justify-content-between">
            <Card.Title>Impressions By Day</Card.Title>
          </div>
          <Card.Body className="p-0">
            <div className="px-3 mt-sm-0 mt-3">
              <ImpressionsByNetwork key={networkRange} series={data["impressions-by-network"]} />
            </div>
          </Card.Body>
        </Card>
      </Row>

      <Row className="flex-grow-1">
        <Card className="custom-card overflow-hidden mb-0 border border-2">
          <Card.Header className="justify-content-between">
            <Card.Title>Engagements By Network</Card.Title>
          </Card.Header>
          <Card.Body>
            <EngagementsByNetworkBar series={data["engagements-by-network"]} />
          </Card.Body>
        </Card>
      </Row>

      <Row className="flex-grow-1">
        <Card className="custom-card overflow-hidden mb-0 border border-2">
          <div className="card-header justify-content-between">
            <Card.Title>Engagements By Day</Card.Title>
          </div>
          <Card.Body className="p-0">
            <div className="px-3 mt-sm-0 mt-3">
              <EngagementsByDay series={data["engagements-by-day"]} />
            </div>
          </Card.Body>
        </Card>
      </Row>

      <Divider title="Reach" note="by network" />

      <Row className="flex-grow-1">
        <Card className="custom-card overflow-hidden mb-0 border border-2">
          <Card.Header>
            <Card.Title>Reach By Network</Card.Title>
          </Card.Header>
          <Card.Body>
            <ReachByNetwork data={data} />
          </Card.Body>
        </Card>
      </Row>
    </Container>
  )
}

export default Conversions
